/**
 * Library sort + date-range helpers — shared by Library Videos and Library
 * Posts (2026-09-14). Pure and DOM-free so the contracts are unit-tested in
 * node alongside the server suite (see vitest.config.ts include).
 *
 * The operator's ask: open a Library page and see the OLD content first so it
 * can be recycled (old posts re-scheduled, old clips re-posted) instead of
 * generating new work — and narrow to a specific window when needed.
 *
 * Two deliberate calls:
 *  - The sort order is a REMEMBERED preference (per page, browser-local):
 *    flip it once and it sticks. See {@link readSortPref}.
 *  - The date range is NOT remembered. A persisted range would silently hide
 *    content on the next visit, which reads as "my posts disappeared".
 *
 * Date axis is whatever date the card already shows (videos: `createdAt`,
 * posts: `generatedAt`) — the caller passes the accessor, so this file knows
 * nothing about either type.
 */

export type SortOrder = "newest" | "oldest";

/** Values straight from two `<input type="date">` fields — "YYYY-MM-DD" or
 *  empty. Either bound may be absent. */
export interface DateRange {
  from?: string;
  to?: string;
}

/** Stable sort by a numeric date, never mutating the input. Ties keep their
 *  incoming order so a same-second batch doesn't shuffle on every toggle. */
export function sortByDate<T>(
  items: readonly T[],
  dateOf: (item: T) => number,
  order: SortOrder,
): T[] {
  const sign = order === "oldest" ? 1 : -1;
  return items
    .map((item, idx) => ({ item, idx, t: dateOf(item) }))
    .sort((a, b) => sign * (a.t - b.t) || a.idx - b.idx)
    .map((r) => r.item);
}

/**
 * Local-calendar-day bounds for a "YYYY-MM-DD" input value, or null when the
 * value is empty/garbage. Parsed as LOCAL time on purpose: `new Date("2026-09-01")`
 * is UTC midnight and shifts by the zone offset, so a range typed as Sept 1
 * would exclude the operator's own Sept 1 morning in any zone east of UTC.
 */
export function localDayBounds(value: string | undefined): { start: number; end: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? "").trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const start = new Date(y, mo - 1, d, 0, 0, 0, 0);
  // Reject a value the Date constructor "helpfully" rolled over (2026-13-40).
  if (start.getFullYear() !== y || start.getMonth() !== mo - 1 || start.getDate() !== d) return null;
  const end = new Date(y, mo - 1, d, 23, 59, 59, 999);
  return { start: start.getTime(), end: end.getTime() };
}

/** Inclusive check against a local-day range. An invalid bound is IGNORED
 *  (treated as unset) rather than failing every row — a half-typed date must
 *  never blank the page. */
export function inDateRange(ms: number, range: DateRange): boolean {
  const from = localDayBounds(range.from);
  const to = localDayBounds(range.to);
  if (from && ms < from.start) return false;
  if (to && ms > to.end) return false;
  return true;
}

/** Filter to the range, then sort. The single entry point both pages use. */
export function applyDateView<T>(
  items: readonly T[],
  dateOf: (item: T) => number,
  order: SortOrder,
  range: DateRange,
): T[] {
  const kept = items.filter((item) => inDateRange(dateOf(item), range));
  return sortByDate(kept, dateOf, order);
}

/** True when a range has at least one usable bound — drives the "clear ×". */
export function hasDateRange(range: DateRange): boolean {
  return localDayBounds(range.from) !== null || localDayBounds(range.to) !== null;
}

// ── Remembered sort preference ──────────────────────────────────────────────
// localStorage is per-browser and can throw (private mode, blocked storage),
// so every access is guarded and the page renders correctly with no stored
// value. Keys are namespaced per page so Videos and Posts remember separately.

export const SORT_PREF_KEYS = {
  videos: "ql.library.videos.sort",
  posts: "ql.library.posts.sort",
} as const;

export function readSortPref(key: string, fallback: SortOrder = "newest"): SortOrder {
  try {
    const v = globalThis.localStorage?.getItem(key);
    return v === "oldest" || v === "newest" ? v : fallback;
  } catch {
    return fallback;
  }
}

export function writeSortPref(key: string, order: SortOrder): void {
  try {
    globalThis.localStorage?.setItem(key, order);
  } catch {
    /* best-effort — the toggle still works for this visit */
  }
}
